import React, { useRef, useEffect, useState, useCallback } from 'react';
import './VideoShowcase.css';

function VideoShowcase() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track video progress for mobile zoom effect (throttled to ~10fps)
  const lastUpdateRef = useRef(0);
  const handleTimeUpdate = useCallback(() => {
    const now = Date.now();
    if (now - lastUpdateRef.current < 100) return;
    lastUpdateRef.current = now;
    if (videoRef.current) {
      const progress = videoRef.current.currentTime / videoRef.current.duration;
      setVideoProgress(progress);
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video && isMobile) {
      video.addEventListener('timeupdate', handleTimeUpdate);
      return () => video.removeEventListener('timeupdate', handleTimeUpdate);
    }
  }, [isMobile, handleTimeUpdate]);

  // Calculate mobile zoom transform based on video progress
  const getMobileZoomStyle = useCallback(() => {
    if (!isMobile) return {};

    // Zoom parameters
    const zoomScale = 2.0; // How much to zoom in
    const translateX = -18; // Pan to show right 2/3 (negative = show right side)
    const translateY = -5; // Slight vertical adjustment

    // Timeline:
    // 0-10%: ease into zoom
    // 10-65%: stay zoomed
    // 65-100%: ease out of zoom

    let scale = 1;
    let tx = 0;
    let ty = 0;

    if (videoProgress <= 0.10) {
      // Ease in to zoom (0% to 10%)
      const t = videoProgress / 0.10;
      const eased = t * t * (3 - 2 * t); // smoothstep
      scale = 1 + (zoomScale - 1) * eased;
      tx = translateX * eased;
      ty = translateY * eased;
    } else if (videoProgress <= 0.65) {
      // Stay zoomed (10% to 65%)
      scale = zoomScale;
      tx = translateX;
      ty = translateY;
    } else {
      // Ease out of zoom (65% to 100%)
      const t = (videoProgress - 0.65) / 0.35;
      const eased = t * t * (3 - 2 * t); // smoothstep
      scale = zoomScale - (zoomScale - 1) * eased;
      tx = translateX * (1 - eased);
      ty = translateY * (1 - eased);
    }

    return {
      transform: `scale(${scale}) translate(${tx}%, ${ty}%)`,
      transformOrigin: 'center center',
    };
  }, [isMobile, videoProgress]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);

        // Play/pause based on visibility for performance
        if (videoRef.current) {
          if (entry.isIntersecting) {
            videoRef.current.play().catch(() => {});
          } else {
            videoRef.current.pause();
          }
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={containerRef}
      className={`video-showcase ${isVisible ? 'visible' : ''}`}
    >
      {/* Top gradient fade */}
      <div className="video-showcase-fade video-showcase-fade-top" />

      {/* Title section */}
      <div id="state-explorer" className="video-showcase-header">
        <h2 className="video-showcase-title">State Explorer</h2>
        <div className="video-showcase-subtitle-wrapper">
          <p className="video-showcase-subtitle">
            <strong>AI discovers your application and builds the map.</strong><br />
            Navigate your app states visually and let AI suggest tests.
          </p>
          {/* Playful hand-drawn callout */}
          <div className="video-showcase-callout">
            <svg className="video-showcase-callout-arrow" viewBox="0 0 60 50" fill="none">
              <path d="M55 10 Q45 15 35 25 Q25 35 15 45" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
              <path d="M20 48 L13 47 L12 40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
            <span className="video-showcase-callout-text">AI drew this map, not us</span>
          </div>
        </div>
      </div>

      {/* Video container with subtle zoom */}
      <div className="video-showcase-wrapper" style={getMobileZoomStyle()}>
        <video
          ref={videoRef}
          className="video-showcase-video"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
        >
          <source src="/explorer.mp4" type="video/mp4" />
        </video>
      </div>
    </section>
  );
}

export default VideoShowcase;
